import React, { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';

export type DynamicStatsChartSelection = 'vendorsFunded' | 'totalAmountFunded' | 'averageDaysAccelerated';

type Props = {
  selectedChart: DynamicStatsChartSelection;
};

const DynamicStatsColumnChart = ({ selectedChart }: Props) => {
  const chartSeries = useMemo(() => {
    switch (selectedChart) {
      case 'vendorsFunded':
        return [{
          name: 'Suppliers Funded',
          data: [
            974,
            1201,
            1102,
            1322,
            1521,
            1648,
            1524,
            1631,
            1732,
            1765,
            1834,
            1824
          ],
        }];
      case 'totalAmountFunded':
        return [
          {
            name: 'Total Amount Funded',
            data: [
              18810184.49,
              28768517.45,
              23236110.25,
              30981480.34,
              37620368.98,
              40939813.30,
              34300924.66,
              37620368.98,
              42046294.74,
              46472220.50,
              56430553.47,
              50898146.26
            ]
          }
        ];
      case 'averageDaysAccelerated':
        return [{
          name: 'Average Days Accelerated',
          data: [
            48.9,
            46.3,
            50.0,
            55.8,
            61.0,
            53.7,
            61.0,
            52.1,
            47.9,
            62.6,
            45.8,
            52.6,
          ],
        }];
      default: throw new Error('Invalid chart selection');
    }
  }, [selectedChart]);

  const yAxis = useMemo(() => {
    switch (selectedChart) {
      case 'vendorsFunded':
      case 'averageDaysAccelerated':
        return {
          labels: {
            formatter: (value: number) => {
              return value % 1 === 0 ? value.toFixed(0) : value.toFixed(2);
            },
          },
        };
      case 'totalAmountFunded':
        return {
          labels: {
            formatter: (value: number) => {
              return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
            },
          },
        };
      default:
        return {};
    }
  }, [selectedChart]);

  const chartOptions = {
    series: chartSeries,
    options: {
      chart: {
        type: 'bar',
        stacked: false,
        toolbar: {
          show: false,
        },
      },
      legend: {
        show: false,
      },
      dataLabels: {
        enabled: false,
      },
      colors: ['#5A05FF', '#743BFF', '#E9EAEB'],
      plotOptions: {
        bar: {
          horizontal: false,
          borderRadius: 4,
          borderRadiusApplication: 'end',
          borderRadiusWhenStacked: 'last',
          columnWidth: 40,
          dataLabels: {
            total: {
              enabled: false,
            },
          },
        },
      },
      xaxis: {
        categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      },
      yaxis: {
        ...yAxis,
        labels: {
          ...yAxis.labels,
          minWidth: 80,
          maxWidth: 80,
        },
      },
      fill: {
        opacity: 1,
      },
    },
  };

  return (
    <div>
      <div id="chart">
        <ReactApexChart options={chartOptions.options as any} series={chartOptions.series} type="bar" height={240} />
      </div>
    </div>
  );
}

function DynamicStats({ selectedChart }: Props): React.ReactElement {
  return (
    <DynamicStatsColumnChart selectedChart={selectedChart} />
  );
}

export default DynamicStats;
